<template>
  <AppLayout>
    <div class="mb-6 flex items-center gap-4 flex-wrap">
      <Button
        v-tooltip.bottom="'List view options'"
        variant="text"
        severity="secondary"
        icon="pi pi-sliders-h"
        rounded
        aria-label="List view options"
        @click="adjustPopover.toggle($event)"
      />

      <Button
        v-tooltip.bottom="attentionFilter === 'true' ? 'Show all' : 'Show attention only'"
        icon="pi pi-exclamation-triangle"
        variant="text"
        rounded
        :severity="attentionFilter === 'true' ? 'danger' : 'secondary'"
        aria-label="Attention only"
        @click="attentionFilter = attentionFilter === 'true' ? 'false' : 'true'"
      />

      <Button
        v-if="auth.user.value?.superAdmin"
        v-tooltip.bottom="pendingFilter === 'true' ? 'Show all' : 'Show pending only'"
        icon="pi pi-hourglass"
        variant="text"
        rounded
        :severity="pendingFilter === 'true' ? 'info' : 'secondary'"
        aria-label="Pending only"
        @click="pendingFilter = pendingFilter === 'true' ? 'false' : 'true'"
      />

      <Select
        v-model="datePreset"
        :options="datePresetOptions"
        optionLabel="label"
        optionValue="value"
        class="w-40"
      />
      <DatePicker
        v-if="datePreset === 'date'"
        v-model="customDate"
        placeholder="Date"
        dateFormat="yy-mm-dd"
        showIcon
        showButtonBar
        class="w-40"
      />
      <template v-if="datePreset === 'custom'">
        <DatePicker
          v-model="customFromDate"
          placeholder="From"
          dateFormat="yy-mm-dd"
          showIcon
          showButtonBar
          class="w-40"
        />
        <DatePicker
          v-model="customToDate"
          placeholder="To"
          dateFormat="yy-mm-dd"
          showIcon
          showButtonBar
          class="w-40"
        />
      </template>

      <div class="ml-auto flex items-center">
        <Button
          variant="text"
          severity="secondary"
          icon="pi pi-ellipsis-v"
          rounded
          aria-label="More actions"
          @click="actionsMenu?.toggle($event)"
        />
        <Menu ref="actionsMenu" :model="actionsMenuItems" :popup="true" />
      </div>
    </div>

    <Popover ref="adjustPopover" class="product-list-adjust-popover">
      <div class="flex flex-col gap-4 min-w-56">
        <div class="hidden md:block">
          <label class="text-sm font-medium text-surface-700 dark:text-surface-300 block mb-2">
            Image size
          </label>
          <Slider v-model="listImageSize" :min="50" :max="200" :step="5" class="w-full" />
          <div class="text-xs text-surface-500 mt-1">{{ listImageSize }}%</div>
        </div>
        <div>
          <label class="text-sm font-medium text-surface-700 dark:text-surface-300 block mb-2">
            View
          </label>
          <SelectButton
            v-model="viewType"
            :options="viewTypeOptions"
            optionLabel="label"
            optionValue="value"
          />
        </div>
        <div class="flex items-center justify-between gap-2">
          <label
            for="group-by-date-toggle"
            class="text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Group by date
          </label>
          <ToggleSwitch id="group-by-date-toggle" v-model="groupByDate" />
        </div>
      </div>
    </Popover>

    <Paginator
      :first="currentOffset"
      :rows="currentLimit"
      :totalRecords="totalRecords"
      :rowsPerPageOptions="[50, 100, 200]"
      @page="onPage"
      class="mb-4"
    />

    <div v-if="loading" class="flex justify-center p-8">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <template v-else v-for="section in displaySections" :key="section.date ?? 'all'">
      <h2 v-if="section.date" class="mb-4">{{ section.date }}</h2>

      <div v-if="section.products.length === 0" class="text-surface-500 mb-4">(No products)</div>

      <!-- Grid view -->
      <div
        v-else-if="viewType === 'grid'"
        class="flex flex-row flex-wrap gap-2 mb-4 product-list-grid"
        :style="{
          '--list-image-size': `${listImageSizePx}px`,
        }"
      >
        <div
          v-for="product in section.products"
          :key="product.id"
          class="product-card-wrapper group list-card"
        >
          <div class="overflow-hidden">
            <RouterLink :to="`/products/${product.id}`" class="no-underline block">
              <div
                class="aspect-square overflow-hidden rounded-lg p-2 md:p-3 flex items-center justify-center box-border"
                :class="
                  product.hasAttention || product.hasFailedTasks
                    ? 'bg-red-500/40'
                    : product.hasPendingTasks
                      ? 'bg-blue-500/40'
                      : 'bg-surface-800'
                "
              >
                <img
                  :src="getImageUrl(product.imageUrl, product.imageContentType, listImageSizePx)"
                  loading="lazy"
                  class="max-w-full max-h-full w-auto h-auto object-contain align-middle"
                />
              </div>
            </RouterLink>
            <div class="px-2 md:pt-1.5 flex items-center justify-between gap-1 min-w-0">
              <span
                class="flex items-center gap-1 text-xs text-surface-500 dark:text-surface-400 shrink-0"
              >
                <i class="pi pi-images" />
                {{ product.imageCount }}
              </span>
              <div class="flex items-center shrink-0">
                <Button
                  @click.prevent="confirmDeleteProduct(product)"
                  icon="pi pi-trash"
                  text
                  rounded
                  size="small"
                  class="transition-opacity opacity-0 group-hover:opacity-100 text-surface-400 dark:text-surface-500"
                  severity="secondary"
                />
                <Button
                  @click.prevent="openWorkflowDialog(product)"
                  icon="pi pi-sitemap"
                  text
                  rounded
                  size="small"
                  class="transition-opacity"
                  :class="{
                    'text-red-500': product.hasFailedTasks,
                    'text-blue-500':
                      !product.hasFailedTasks && product.hasPendingTasks,
                    'text-surface-400 dark:text-surface-500 opacity-0 group-hover:opacity-100':
                      !product.hasFailedTasks && !product.hasPendingTasks,
                  }"
                  :severity="
                    product.hasFailedTasks
                      ? 'danger'
                      : product.hasPendingTasks
                        ? 'info'
                        : 'secondary'
                  "
                />
                <Button
                  @click.prevent="toggleAttention(product)"
                  icon="pi pi-exclamation-triangle"
                  text
                  rounded
                  size="small"
                  class="transition-opacity"
                  :class="{
                    'text-red-500': product.hasAttention,
                    'text-surface-400 dark:text-surface-500 opacity-0 group-hover:opacity-100':
                      !product.hasAttention,
                  }"
                  :severity="product.hasAttention ? 'danger' : 'secondary'"
                />
              </div>
            </div>
            <RouterLink :to="`/products/${product.id}`" class="no-underline block px-2 pb-2">
              <div
                class="text-sm text-surface-400 dark:text-surface-0 truncate"
                :title="product.name || product.code || ''"
              >
                {{ product.name || product.code || '\u00A0' }}
              </div>
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- Table view -->
      <div v-else class="mb-4 overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b border-surface-200 dark:border-surface-700">
              <th
                class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400 w-0"
              >
                Image
              </th>
              <th class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400">
                Name
              </th>
              <th class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400">
                Photos
              </th>
              <th class="w-10 py-2"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="product in section.products"
              :key="product.id"
              class="border-b border-surface-100 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800/50"
              :class="{
                'bg-red-50/50 dark:bg-red-950/20 border-l-2 border-l-red-400/60':
                  product.hasAttention || product.hasFailedTasks,
                'bg-blue-50/50 dark:bg-blue-950/20 border-l-2 border-l-blue-400/60':
                  !product.hasAttention &&
                  !product.hasFailedTasks &&
                  product.hasPendingTasks,
              }"
            >
              <td class="py-2 pr-2 align-middle">
                <RouterLink :to="`/products/${product.id}`" class="block w-fit">
                  <div
                    class="overflow-hidden rounded-lg list-card-image aspect-square shrink-0"
                    :style="{ width: `${listImageSizePx}px`, height: `${listImageSizePx}px` }"
                  >
                    <img
                      :src="
                        getImageUrl(product.imageUrl, product.imageContentType, listImageSizePx)
                      "
                      loading="lazy"
                      class="w-full h-full object-contain rounded-lg"
                    />
                  </div>
                </RouterLink>
              </td>
              <td class="py-2 pr-2 align-middle">
                <RouterLink
                  :to="`/products/${product.id}`"
                  class="text-surface-900 dark:text-surface-0 hover:underline no-underline"
                >
                  {{ product.name || product.code || '—' }}
                </RouterLink>
              </td>
              <td class="py-2 pr-2 align-middle text-surface-500 dark:text-surface-400">
                <span class="flex items-center gap-1">
                  <i class="pi pi-images" />
                  {{ product.imageCount }}
                </span>
              </td>
              <td class="py-2 align-middle">
                <div class="flex items-center">
                  <Button
                    @click.prevent="openWorkflowDialog(product)"
                    icon="pi pi-sitemap"
                    text
                    rounded
                    size="small"
                    :class="{
                      'text-red-500': product.hasFailedTasks,
                      'text-blue-500':
                        !product.hasFailedTasks &&
                        product.hasPendingTasks,
                      'text-surface-400 dark:text-surface-500':
                        !product.hasFailedTasks &&
                        !product.hasPendingTasks,
                    }"
                    :severity="
                      product.hasFailedTasks
                        ? 'danger'
                        : product.hasPendingTasks
                          ? 'info'
                          : 'secondary'
                    "
                  />
                  <Button
                    @click.prevent="toggleAttention(product)"
                    icon="pi pi-exclamation-triangle"
                    text
                    rounded
                    size="small"
                    :class="{
                      'text-red-500': product.hasAttention,
                      'text-surface-400 dark:text-surface-500': !product.hasAttention,
                    }"
                    :severity="product.hasAttention ? 'danger' : 'secondary'"
                  />
                  <Button
                    @click.prevent="confirmDeleteProduct(product)"
                    icon="pi pi-trash"
                    text
                    rounded
                    size="small"
                    class="text-surface-400 dark:text-surface-500"
                    severity="secondary"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <Paginator
      :first="currentOffset"
      :rows="currentLimit"
      :totalRecords="totalRecords"
      :rowsPerPageOptions="[50, 100, 200]"
      @page="onPage"
      class="my-4"
    />

    <UploadImagesDialog v-model:visible="uploadFixedImagesDialogVisible" />
    <ProductWorkflowDialog
      v-if="workflowDialogProductId"
      v-model:visible="workflowDialogVisible"
      :product-id="workflowDialogProductId"
    />
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import AppLayout from '@/components/AppLayout.vue';
import dayjs from 'dayjs';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import Paginator from 'primevue/paginator';
import Popover from 'primevue/popover';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import SelectButton from 'primevue/selectbutton';
import Slider from 'primevue/slider';
import ToggleSwitch from 'primevue/toggleswitch';
import { useConfirm } from 'primevue/useconfirm';
import type { ListProductsEntryDto } from '@/api/generated';
import { groupBy } from 'lodash';
import { useApiClient } from '@/api/apiClient';
import { useAuth } from '@/modules/auth/auth';
import UploadImagesDialog from '@/modules/products/upload-images-dialog/UploadImagesDialog.vue';
import ProductWorkflowDialog from '@/modules/products/workflow/ProductWorkflowDialog.vue';

const { api } = useApiClient();
const auth = useAuth();
const confirm = useConfirm();

const loading = ref(true);
const error = ref('');
const products = ref<ListProductsEntryDto[]>([]);

const attentionFilter = ref<string>(localStorage.getItem('productListAttention') || 'false');
watch(attentionFilter, val => localStorage.setItem('productListAttention', val));

const pendingFilter = ref<string>(localStorage.getItem('productListPending') || 'false');
watch(pendingFilter, val => localStorage.setItem('productListPending', val));

// Pagination
const currentOffset = ref(0);
const currentLimit = ref(50);
const totalRecords = ref(0);

// Date filter
const datePresetOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'last-7-days' },
  { label: 'Last 30 days', value: 'last-30-days' },
  { label: 'Date', value: 'date' },
  { label: 'Date range', value: 'custom' },
];
const datePreset = ref(localStorage.getItem('productListDatePreset') || 'last-7-days');
watch(datePreset, val => localStorage.setItem('productListDatePreset', val));
const storedDate = localStorage.getItem('productListCustomDate');
const customDate = ref<Date | null>(storedDate ? new Date(storedDate) : null);
watch(customDate, val =>
  val
    ? localStorage.setItem('productListCustomDate', val.toISOString())
    : localStorage.removeItem('productListCustomDate'),
);

const storedFromDate = localStorage.getItem('productListCustomFromDate');
const storedToDate = localStorage.getItem('productListCustomToDate');
const customFromDate = ref<Date | null>(storedFromDate ? new Date(storedFromDate) : null);
const customToDate = ref<Date | null>(storedToDate ? new Date(storedToDate) : null);
watch(customFromDate, val =>
  val
    ? localStorage.setItem('productListCustomFromDate', val.toISOString())
    : localStorage.removeItem('productListCustomFromDate'),
);
watch(customToDate, val =>
  val
    ? localStorage.setItem('productListCustomToDate', val.toISOString())
    : localStorage.removeItem('productListCustomToDate'),
);

const dateRange = computed(() => {
  const now = dayjs();
  switch (datePreset.value) {
    case 'today':
      return { from: now.startOf('day').toISOString(), to: now.endOf('day').toISOString() };
    case 'yesterday': {
      const yesterday = now.subtract(1, 'day');
      return { from: yesterday.startOf('day').toISOString(), to: yesterday.endOf('day').toISOString() };
    }
    case 'last-7-days':
      return { from: now.subtract(7, 'day').startOf('day').toISOString(), to: now.endOf('day').toISOString() };
    case 'last-30-days':
      return { from: now.subtract(30, 'day').startOf('day').toISOString(), to: now.endOf('day').toISOString() };
    case 'date':
      return {
        from: customDate.value ? dayjs(customDate.value).startOf('day').toISOString() : undefined,
        to: customDate.value ? dayjs(customDate.value).endOf('day').toISOString() : undefined,
      };
    case 'custom':
      return {
        from: customFromDate.value ? dayjs(customFromDate.value).startOf('day').toISOString() : undefined,
        to: customToDate.value ? dayjs(customToDate.value).endOf('day').toISOString() : undefined,
      };
    default:
      return { from: undefined, to: undefined };
  }
});

const uploadFixedImagesDialogVisible = ref(false);
const workflowDialogProductId = ref<string | null>(null);
const workflowDialogVisible = ref(false);

function openWorkflowDialog(product: ListProductsEntryDto) {
  workflowDialogProductId.value = product.id;
  workflowDialogVisible.value = true;
}

const adjustPopover = ref();
const actionsMenu = ref<InstanceType<typeof Menu>>();

const actionsMenuItems = computed((): MenuItem[] => [
  ...(auth.user.value?.superAdmin
    ? [
        {
          label: 'Download Problematic Images',
          icon: 'pi pi-download',
          command: () =>
            window.open('/api/images/download-problematic-images', '_blank', 'noopener,noreferrer'),
        },
      ]
    : []),
  {
    label: 'Upload Fixed Images',
    icon: 'pi pi-upload',
    command: () => (uploadFixedImagesDialogVisible.value = true),
  },
]);

const listImageSize = ref(Number(localStorage.getItem('productListImageSize')) || 100); // 100% = 150px, range 50–200%
watch(listImageSize, val => localStorage.setItem('productListImageSize', String(val)));
const listImageSizePx = computed(() => Math.round((listImageSize.value / 100) * 230));
const viewTypeOptions = [
  { label: 'Grid', value: 'grid' },
  { label: 'Table', value: 'table' },
];
const viewType = ref<'grid' | 'table'>('grid');
const groupByDate = ref(true);

// Watch for filter changes and reset pagination
watch(attentionFilter, () => {
  currentOffset.value = 0;
  loadProducts();
});

watch(pendingFilter, () => {
  currentOffset.value = 0;
  loadProducts();
});

watch([datePreset, customDate, customFromDate, customToDate], () => {
  currentOffset.value = 0;
  loadProducts();
});

function onPage(event: { first: number; rows: number }) {
  currentOffset.value = event.first;
  currentLimit.value = event.rows;
  loadProducts();
}

async function loadProducts() {
  try {
    loading.value = true;
    const { from, to } = dateRange.value;
    const { data, error: err } = await api.productControllerList({
      body: {
        offset: currentOffset.value,
        limit: currentLimit.value,
        attention: attentionFilter.value === 'true',
        pending: pendingFilter.value === 'true',
        from,
        to,
      },
    });
    if (err) {
      throw new Error('Failed to load products');
    }
    products.value = data?.data ?? [];
    totalRecords.value = data?.total ?? 0;
  } catch (err) {
    error.value = 'Failed to load products';
    throw err;
  } finally {
    loading.value = false;
  }
}

const productsByDay = computed(() => {
  const res = groupBy(products.value, p => dayjs(p.created).format('YYYY-MM-DD'));
  const entries = Object.entries(res).map(([date, products]) => ({
    date,
    products,
  }));
  return entries;
});

const displaySections = computed(() => {
  if (groupByDate.value) {
    return productsByDay.value;
  }
  return [{ date: null as string | null, products: products.value }];
});

loadProducts();

async function toggleAttention(product: ListProductsEntryDto) {
  const attention = !product.hasAttention;
  const { error } = await api.productControllerSetAttention({
    path: {
      productId: product.id,
    },
    body: {
      attention,
    },
  });
  if (error) {
    throw new Error('Failed to set attention');
  }
  product.hasAttention = attention;
}

function confirmDeleteProduct(product: ListProductsEntryDto) {
  confirm.require({
    message: `Are you sure you want to delete "${product.name || product.code}"?`,
    header: 'Delete Product',
    icon: 'pi pi-trash',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => deleteProduct(product),
  });
}

async function deleteProduct(product: ListProductsEntryDto) {
  const { error } = await api.productControllerDelete({
    path: {
      productId: product.id,
    },
  });
  if (error) {
    throw new Error('Failed to delete product');
  }
  products.value = products.value.filter(p => p.id !== product.id);
}

function getImageUrl(imageUrl: string, contentType: string, width: number): string | undefined {
  const widthPx = devicePixelRatio * width;
  let url = `${imageUrl}=s${widthPx}`;
  if (contentType === 'image/webp') {
    url += '-rw';
  }
  return url;
}

// async function onProductCreated() {
//   // TODO: how to reset dialog state?
//   reset.value = true;
//   nextTick(() => {
//     reset.value = false;
//   });
// }
</script>

<style scoped>
.product-list-grid .list-card {
  width: var(--list-image-size);
  min-width: var(--list-image-size);
}

/* Mobile: at least 3 per row; respect image size slider (smaller size = more columns) */
@media (max-width: 767px) {
  .product-list-grid .list-card {
    width: min(var(--list-image-size), calc((100% - 1rem) / 3));
    min-width: min(var(--list-image-size), calc((100% - 1rem) / 3));
  }
}
</style>
